import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import moment from "moment";
import { useGetReportTable } from "api/reports/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { useUpdateReport } from "api/reports/service";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import { Button, Card, CardTitle, Col, Row } from "reactstrap";
import { getReportSurfaceColors } from "./reportDetailTheme";
import {
  getDynamicDateOption,
  normalizeFilterValueForOperator,
  REPORT_FILTER_DYNAMIC_DATE_OPTIONS,
} from "./reportDynamicDateOptions";

const ReportFilters = forwardRef(
  ({ activeColor = "blue", isTheme2 = false, id, onFiltersChange, data, permissions = [] }, ref) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [expandedTables, setExpandedTables] = useState([]);
    const [activeFilter, setActiveFilter] = useState("all");
    const [closeSourceArea, setCloseSourceArea] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(() => {
      const darkMode = localStorage.getItem("darkMode");
      return darkMode ? JSON.parse(darkMode) : true;
    });
    const [isLightTheme, setIsLightTheme] = useState(false);

    const { data: reportTableData, isLoading } = useGetReportTable(id);
    const updateReportMutation = useUpdateReport();

    const activeColorStyle = useMemo(
      () => getActiveColorStyle(activeColor),
      [activeColor]
    );
    const selectStyles = useMemo(
      () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
      [activeColorStyle, isTheme2]
    );
    const dateInputStyle = isTheme2 ? { fontSize: "14px" } : undefined;
    const getFieldTypeIcon = (type) => {
      const baseColor = isLightTheme ? "#6c757d" : "#ffffff";
      const lightBg = isLightTheme ? "#e9ecef" : baseColor + "20";

      const iconMap = {
        text: { icon: "fas fa-font", color: baseColor, bg: lightBg },
        number: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
        id: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
        date: { icon: "fas fa-calendar", color: baseColor, bg: lightBg },
        currency: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
        price: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
        percentage: {
          icon: "fas fa-percent",
          color: baseColor,
          bg: lightBg,
        },
        percent: {
          icon: "fas fa-percent",
          color: baseColor,
          bg: lightBg,
        },
        phone: { icon: "fas fa-phone", color: baseColor, bg: lightBg },
      };

      const config = iconMap[type] || iconMap.text;
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: "24px",
            height: "24px",
            backgroundColor: config.bg,
            color: config.color,
            fontSize: "10px",
          }}
        >
          <i className={config.icon}></i>
        </div>
      );
    };

    const toggleTableExpansion = (tableName) => {
      setExpandedTables((prev) => ({
        ...prev,
        [tableName]: !prev[tableName],
      }));
    };
    // Theme detection
    useEffect(() => {
      const checkTheme = () => {
        setIsLightTheme(document.body.classList.contains("white-content"));
      };
      checkTheme();

      const observer = new MutationObserver(checkTheme);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => observer.disconnect();
    }, []);

    // Dark mode detection
    useEffect(() => {
      const handleStorageChange = () => {
        const darkMode = localStorage.getItem("darkMode");
        const newTheme = darkMode ? JSON.parse(darkMode) : true;
        setCurrentTheme(newTheme);
      };

      window.addEventListener("storage", handleStorageChange);

      const interval = setInterval(() => {
        const darkMode = localStorage.getItem("darkMode");
        const newTheme = darkMode ? JSON.parse(darkMode) : true;
        if (newTheme !== currentTheme) {
          setCurrentTheme(newTheme);
        }
      }, 100);

      return () => {
        window.removeEventListener("storage", handleStorageChange);
        clearInterval(interval);
      };
    }, [currentTheme]);

    // Filter operators
    const filterOperators = [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "contains", label: "Contains" },
      { value: "not_contains", label: "Not Contains" },
      { value: "greater_than", label: "Greater Than" },
      { value: "less_than", label: "Less Than" },
      { value: "between", label: "Between" },
      { value: "is_null", label: "Is Null" },
      { value: "is_not_null", label: "Is Not Null" },
    ];

    // Date-specific operators
    const dateOperators = [
      { value: "greater_than", label: "Greater Than" },
      { value: "less_than", label: "Less Than" },
      { value: "greater_than_or_equal", label: "Greater Than or Equal" },
      { value: "less_than_or_equal", label: "Less Than or Equal" },
      { value: "between", label: "Between" },
      { value: "dynamic", label: "Dynamic" },
    ];

    // Enum-specific operators
    const enumOperators = [
      { value: "includes", label: "Includes" },
      { value: "not_includes", label: "Does Not Include" },
    ];

    // Transform data structure for easier use
    const transformedData = useMemo(() => {
      if (!reportTableData?.data?.tables) return {};

      const tables = {};
      Object.keys(reportTableData.data.tables).forEach((tableName) => {
        tables[tableName] = reportTableData.data.tables[tableName].map(
          (fieldObj) => {
            const fieldKey = Object.keys(fieldObj)[0];
            const fieldData = fieldObj[fieldKey];
            return {
              id: fieldKey,
              label:
                fieldData.label ||
                (fieldKey
                  ? fieldKey
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())
                  : ""),
              type: fieldData.type || "text",
              value: fieldData.value || fieldKey,
              tableName: tableName,
              enumValues: fieldData.options || fieldData.enum_values || null, // Enum values for dropdown fields
            };
          }
        );
      });
      return tables;
    }, [reportTableData]);

    // Initialize selectedFilters with existing data
    useEffect(() => {
      if (data && data.length > 0) {
        const initializedFilters = data.map((existingFilter, index) => {
          const fieldData = Object.values(transformedData)
            .flat()
            .find((field) => field.id === existingFilter.field_name);
          return {
            id: existingFilter.id || `filter_${index}`,
            field_name: existingFilter.field_name,
            operator: existingFilter.operator || "equals",
            value: existingFilter.value || "",
            table_name: existingFilter.table_name,
            label: fieldData?.label || existingFilter.field_name,
            type: fieldData?.type || "text",
            enumValues: fieldData?.enumValues || existingFilter.enum_values || null,
            is_active: existingFilter.is_active !== undefined ? existingFilter.is_active : true,
          };
        });
        setSelectedFilters(initializedFilters);
      } else if (data && data.length === 0) {
        // Clear filters if empty array
        setSelectedFilters([]);
      }
    }, [data, transformedData]);

    // Keep enum options fresh when report-table options load/update (e.g. Flag ID).
    useEffect(() => {
      if (!Object.keys(transformedData).length) return;
      setSelectedFilters((prev) => {
        let changed = false;
        const next = prev.map((filter) => {
          const fieldData = Object.values(transformedData)
            .flat()
            .find((field) => field.id === filter.field_name);
          if (!fieldData?.enumValues) return filter;
          const prevOptions = Array.isArray(filter.enumValues) ? filter.enumValues : [];
          const nextOptions = fieldData.enumValues;
          if (
            prevOptions.length === nextOptions.length &&
            prevOptions.every((value, index) => value === nextOptions[index])
          ) {
            return filter;
          }
          changed = true;
          return {
            ...filter,
            enumValues: nextOptions,
            type: fieldData.type || filter.type,
            label: fieldData.label || filter.label,
          };
        });
        return changed ? next : prev;
      });
    }, [transformedData]);

    useEffect(() => {
      // Global isActive removed as per user request to handle individual filter activity
    }, [reportTableData]);

    // Add filter function
    const addFilter = (field) => {
      const newFilter = {
        id: `filter_${Date.now()}`,
        field_name: field.id,
        operator: field.enumValues
          ? "includes"
          : field.type === "date"
            ? ">"
            : "equals",
        value: field.enumValues ? [] : "", // Enum için boş başla
        table_name: field.tableName,
        label: field.label,
        type: field.type,
        enumValues: field.enumValues,
        is_active: true,
      };

      const updatedFilters = [...selectedFilters, newFilter];
      setSelectedFilters(updatedFilters);
      onFiltersChange?.(updatedFilters);
    };

    // Remove filter function
    const removeFilter = (filterId) => {
      const updatedFilters = selectedFilters.filter(
        (filter) => filter.id !== filterId
      );
      setSelectedFilters(updatedFilters);
      onFiltersChange?.(updatedFilters);
    };

    // Update filter function
    const updateFilter = (filterId, field, value) => {
      const updatedFilters = selectedFilters.map((filter) =>
        filter.id === filterId ? { ...filter, [field]: value } : filter
      );
      setSelectedFilters(updatedFilters);
      onFiltersChange?.(updatedFilters);
    };

    const updateFilterOperator = (filter, nextOperator) => {
      const updatedFilters = selectedFilters.map((item) =>
        item.id === filter.id
          ? {
              ...item,
              operator: nextOperator,
              value: normalizeFilterValueForOperator(item, nextOperator),
            }
          : item
      );
      setSelectedFilters(updatedFilters);
      onFiltersChange?.(updatedFilters);
    };


    const filteredFields = useMemo(() => {
      let filtered = transformedData;

      // Apply type filter
      if (activeFilter !== "all") {
        const typeMap = {
          text: "text",
          number: ["number", "id"],
          date: "date",
          currency: ["currency", "price"],
          phone: "phone",
          percentage: ["percentage", "percent"],
        };

        const typesToFilter = typeMap[activeFilter];
        if (typesToFilter) {
          const newFiltered = {};
          Object.keys(filtered).forEach((tableName) => {
            newFiltered[tableName] = filtered[tableName].filter((field) =>
              Array.isArray(typesToFilter)
                ? typesToFilter.includes(field.type)
                : field.type === typesToFilter
            );
          });
          filtered = newFiltered;
        }
      }

      // Apply search filter
      if (searchTerm) {
        const searchFiltered = {};
        Object.keys(filtered).forEach((tableName) => {
          searchFiltered[tableName] = filtered[tableName].filter(
            (field) =>
              field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              field.id.toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
        filtered = searchFiltered;
      }

      return filtered;
    }, [transformedData, searchTerm, activeFilter]);

    useEffect(() => {
      if (searchTerm) {
        const newExpanded = {};
        Object.keys(filteredFields).forEach((tableName) => {
          if (filteredFields[tableName] && filteredFields[tableName].length > 0) {
            newExpanded[tableName] = true;
          }
        });
        setExpandedTables((prev) => ({ ...prev, ...newExpanded }));
      }
    }, [searchTerm, filteredFields]);
    // Validate filters before sending
    const validateFilters = () => {
      const errors = [];

      selectedFilters.forEach((filter, index) => {
        if (filter.enumValues) {
          if (!filter.value || filter.value.length === 0) {
            errors.push(
              `Filter ${index + 1} (${filter.label
              }): At least one enum value must be selected`
            );
          }
        }
      });

      return errors;
    };

    // Save handler function
    const handleSave = async (options = {}) => {
      try {
        const validationErrors = validateFilters();
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join("; "));
        }

        const formattedFilters = selectedFilters.map((filter) => {
          const filterData = {
            id: filter.id,
            field_name: filter.field_name,
            operator: filter.operator,
            value: filter.value,
            table_name: filter.table_name,
            label: filter.label,
            type: filter.type,
            is_active: filter.is_active,
          };

          // Include enum values if present
          if (filter.enumValues) {
            filterData.enum_values = filter.enumValues;
          }

          return filterData;
        });

        await updateReportMutation.mutateAsync({
          id: id,
          data: {
            filters: formattedFilters,
            permissons: permissions,
          },
          hideNotification: options.hideNotification,
        });
      } catch (error) {
        console.error("Error saving filters:", error);
        throw error; // Re-throw to let parent handle it
      } finally {
      }
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFilters: () => {
        const validationErrors = validateFilters();
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join("; "));
        }
        return selectedFilters;
      },
      clearFilters: () => {
        setSelectedFilters([]);
        onFiltersChange?.([]);
      },
      validateFilters: validateFilters,
      handleSave: handleSave,
    }));

    const filterButtons = [
      { key: "all", label: "All", icon: "fas fa-th" },
      { key: "text", label: "Text", icon: "fas fa-font" },
      { key: "number", label: "Number", icon: "fas fa-hashtag" },
      { key: "date", label: "Date", icon: "fas fa-calendar" },
      { key: "currency", label: "Currency", icon: "fas fa-dollar-sign" },
      { key: "phone", label: "Phone", icon: "fas fa-phone" },
      { key: "percentage", label: "Percent", icon: "fas fa-percent", showPercent: false },
    ];

    const surface = useMemo(
      () => getReportSurfaceColors(isLightTheme, isTheme2),
      [isLightTheme, isTheme2]
    );

    if (isLoading) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="sr-only">Loading...</span>
            </div>
            <h5 className="text-light">Loading data sources...</h5>
          </div>
        </div>
      );
    }

    return (
      <div>
        <style jsx>{`
          .glass-card {
            border: 1px solid ${surface.border};
            box-shadow: ${isLightTheme
            ? "0 4px 6px rgba(0, 0, 0, 0.1)"
            : "0 4px 6px rgba(0, 0, 0, 0.3)"};
          }

          .modern-search {
            padding: 12px 20px;
            background: ${surface.panelBg};
            color: ${isLightTheme ? "#2d3748" : "white"};
            border: 1px solid ${surface.border};
          }

          .modern-search::placeholder {
            color: #a0aec0;
          }

          .react-datetime-container {
            position: relative;
            z-index: 1040;
          }

          .filter-btn {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 6px 12px;
            margin: 2px;
            transition: all 0.3s ease;
            color: ${isLightTheme ? "#4a5568" : "white"};
          }

          .filter-btn.active {
            background: ${activeColorStyle.borderColor || "#007bff"};
            color: white;
          }

          .table-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .field-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .field-item:hover {
            background: ${isLightTheme ? "#edf2f7" : "#5a6578"};
            transform: translateY(-1px);
            box-shadow: ${isLightTheme
            ? "0 2px 4px rgba(0, 0, 0, 0.1)"
            : "0 8px 25px rgba(0, 0, 0, 0.3)"};
          }

          .field-item.selected {
            background: ${activeColorStyle.backgroundColor || "#007bff"};
            color: white;
          }

          .selected-filters-container {
            border: 1px solid ${surface.border};
            padding: 20px;
            box-shadow: ${isLightTheme
            ? "0 2px 4px rgba(0, 0, 0, 0.1)"
            : "0 4px 6px rgba(0, 0, 0, 0.3)"};
          }

          .filter-card {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 10px;
            margin-bottom: 12px;
            transition: all 0.3s ease;
          }

          .form-check-input {
            width: 18px;
            height: 18px;
            margin-top: 0.25rem;
            margin-right: 0.5rem;
            cursor: pointer;
          }

          .form-check-input:checked {
            background-color: ${activeColorStyle.backgroundColor || "#007bff"};
            border-color: ${activeColorStyle.borderColor || "#007bff"};
          }

          .form-check-input:focus {
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }

          .form-check-label {
            cursor: pointer;
            margin-bottom: 0;
            padding-left: 0.5rem;
          }

          .enum-container {
            border: 2px solid ${surface.border};
            transition: border-color 0.3s ease;
          }

          .enum-container .enum-check-row {
            display: flex;
            align-items: center;
            min-height: 24px;
            margin-bottom: 4px;
          }

          .enum-container .enum-checkbox {
            width: 18px;
            height: 18px;
            min-width: 18px;
            margin: 0 8px 0 0;
            flex-shrink: 0;
            cursor: pointer;
            position: relative;
            top: -2px;
            border-radius: 0 !important;
          }

          .enum-container .enum-checkbox-label {
            cursor: pointer;
            margin-bottom: 0;
            padding-left: 0;
            line-height: 18px;
            user-select: none;
          }

          .enum-container.error {
            border-color: #dc3545 !important;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
        `}</style>
        <Row style={{ position: 'relative', zIndex: 1 }}>
          {/* Left Panel - Available Fields */}
          <Col md={closeSourceArea ? 12 : 6} style={{ position: 'relative', zIndex: 2 }}>
            <Card className="selected-filters-container p-3" style={{ position: 'relative', zIndex: 2 }}>
              <CardTitle
                tag="h5"
                className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"
                  } d-flex justify-content-between align-items-center`}
              >
                <div className="d-flex align-items-center justify-content-center">
                  <i className="fas fa-filter mr-2"></i>
                  Selected Filters
                </div>
                {closeSourceArea && (
                  <Button
                    onClick={() => setCloseSourceArea(!closeSourceArea)}
                    color="link"
                    className="px-2 py-0"
                    style={{ color: isLightTheme ? "black" : "white" }}
                  >
                    <i className="tim-icons icon-simple-add"></i>
                  </Button>
                )}
              </CardTitle>

              {selectedFilters.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-filter fa-3x mb-3 text-muted"></i>
                  <h6 className="text-muted">No filters added yet</h6>
                  <p className="text-muted">
                    Click on a field from the left panel to add a filter
                  </p>
                </div>
              ) : (
                <div>
                  {selectedFilters.map((filter, index) => (
                    <div key={filter.id} className="filter-card">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={filter.is_active}
                            onChange={(e) => updateFilter(filter.id, 'is_active', e.target.checked)}
                            title="Enable/Disable Filter"
                            style={{
                              width: '15px',
                              height: '15px',
                              cursor: 'pointer',
                              accentColor: activeColorStyle.borderColor || '#1f8ef1'
                            }}
                          />
                          <div className="mr-2" style={{ opacity: filter.is_active ? 1 : 0.5 }}>
                            {getFieldTypeIcon(filter.type)}
                          </div>
                          <div className="d-flex flex-column">
                            <h6 className={`mb-0 ${isLightTheme ? "text-dark" : "text-white"}`} style={{
                              opacity: filter.is_active ? 1 : 0.5,
                              textDecoration: filter.is_active ? 'none' : 'line-through'
                            }}>
                              {filter.label}
                            </h6>
                            <span
                              className={isLightTheme ? "text-muted" : "text-light"}
                              style={{
                                opacity: filter.is_active ? 0.6 : 0.3,
                                fontSize: '11px',
                                marginTop: '2px',
                                textDecoration: filter.is_active ? 'none' : 'line-through'
                              }}
                            >
                              {filter.table_name ? filter.table_name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : ''}
                            </span>
                          </div>
                        </div>
                        <Button
                          color="link"
                          onClick={() => removeFilter(filter.id)}
                          className="ml-2 p-0"
                          style={{
                            transition: 'all 0.3s ease',
                            transform: 'scale(1)',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.color = '#dc3545';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.color = '';
                          }}
                        >
                          <i
                            style={{
                              height: "16px",
                              width: "16px",
                            }}
                            className="fas fa-trash text-light"
                          ></i>
                        </Button>
                      </div>
                      <div className="row" style={{ opacity: filter.is_active ? 1 : 0.6 }}>
                        <div className="col-md-6">
                          <label className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"
                            }`}>Operator</label>
                          <Select
                            activeColor={activeColor}
                            className="react-select"
                            placeholder="Select Operator"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={(filter.enumValues
                              ? enumOperators
                              : filter.type === "date"
                                ? dateOperators
                                : filterOperators
                            ).find((op) => op.value === filter.operator)}
                            onChange={(option) =>
                              updateFilterOperator(
                                filter,
                                option?.value ||
                                (filter.enumValues
                                  ? "includes"
                                  : filter.type === "date"
                                    ? "dynamic"
                                    : "equals")
                              )
                            }
                            options={
                              filter.enumValues
                                ? enumOperators
                                : filter.type === "date"
                                  ? dateOperators
                                  : filterOperators
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <label className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"
                            }`}>Value</label>
                          {filter.enumValues ? (
                            <div
                              className={`enum-container p-3 ${!filter.value || filter.value.length === 0
                                ? "error"
                                : ""
                                }`}
                              style={{
                                backgroundColor: isLightTheme
                                  ? "#f7fafc"
                                  : "#4a5568",
                                maxHeight: "200px",
                                overflowY: "auto",
                              }}
                            >
                              <div className="enum-check-row">
                                <input
                                  type="checkbox"
                                  className="enum-checkbox"
                                  id={`selectAll_${filter.id}`}
                                  checked={
                                    Array.isArray(filter.value) &&
                                    filter.value.length ===
                                    filter.enumValues.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      updateFilter(
                                        filter.id,
                                        "value",
                                        filter.enumValues
                                      );
                                    } else {
                                      // Select All kaldırıldığında en az bir değer seçili kalmalı
                                      if (
                                        filter.value &&
                                        filter.value.length > 1
                                      ) {
                                        updateFilter(filter.id, "value", [
                                          filter.value[0],
                                        ]);
                                      }
                                    }
                                  }}
                                />
                                <label
                                  className={`enum-checkbox-label ${isLightTheme ? "text-dark" : "text-white"
                                    }`}
                                  htmlFor={`selectAll_${filter.id}`}
                                >
                                  <strong>Select All</strong>
                                </label>
                              </div>
                              {filter.enumValues.map((option, index) => (
                                <div
                                  key={index}
                                  className="enum-check-row"
                                >
                                  <input
                                    type="checkbox"
                                    className="enum-checkbox"
                                    id={`${filter.id}_${index}`}
                                    checked={
                                      Array.isArray(filter.value) &&
                                      filter.value.includes(option)
                                    }
                                    onChange={(e) => {
                                      const currentValues = Array.isArray(
                                        filter.value
                                      )
                                        ? filter.value
                                        : [];

                                      if (e.target.checked) {
                                        // Yeni değer ekle
                                        updateFilter(filter.id, "value", [
                                          ...currentValues,
                                          option,
                                        ]);
                                      } else {
                                        // Değer kaldır - ama en az bir değer kalmalı
                                        const newValues = currentValues.filter(
                                          (v) => v !== option
                                        );

                                        if (newValues.length === 0) {
                                          // Eğer son değer kaldırılıyorsa, bu değeri zorunlu kıl
                                          alert(
                                            "At least one enum value must be selected!"
                                          );
                                          return;
                                        }

                                        updateFilter(
                                          filter.id,
                                          "value",
                                          newValues
                                        );
                                      }
                                    }}
                                  />
                                  <label
                                    className={`enum-checkbox-label ${isLightTheme ? "text-dark" : "text-white"
                                      }`}
                                    htmlFor={`${filter.id}_${index}`}
                                  >
                                    {option && option.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </label>
                                </div>
                              ))}

                              {/* Validation message */}
                              {(!filter.value || filter.value.length === 0) && (
                                <div className="text-danger mt-2">
                                  <small>
                                    <i className="fas fa-exclamation-triangle mr-1"></i>
                                    At least one enum value must be selected
                                  </small>
                                </div>
                              )}
                            </div>
                          ) : filter.operator === "between" ? (
                            filter.type === "date" ? (
                              <div className="d-flex align-items-center w-100">
                                <div className="react-datetime-container" style={{ flex: 1 }}>
                                  <ReactDatetime
                                    value={
                                      filter.value?.from
                                        ? moment(filter.value.from, "YYYY-MM-DD")
                                        : null
                                    }
                                    onChange={(date) => {
                                      const currentValue = filter.value || {};
                                      updateFilter(filter.id, "value", {
                                        ...currentValue,
                                        from: date && moment.isMoment(date)
                                          ? date.format("YYYY-MM-DD")
                                          : date instanceof Date
                                            ? moment(date).format("YYYY-MM-DD")
                                            : "",
                                      });
                                    }}
                                    dateFormat="YYYY-MM-DD"
                                    timeFormat={false}
                                    inputProps={{
                                      placeholder: "From date...",
                                      className: "form-control",
                                      style: dateInputStyle,
                                    }}
                                  />

                                </div>
                                <span className={`mx-2 ${isLightTheme ? "text-dark" : "text-white"}`} style={{ fontWeight: 500 }}>
                                  to
                                </span>
                                <div className="react-datetime-container" style={{ flex: 1 }}>
                                  <ReactDatetime
                                    value={
                                      filter.value?.to
                                        ? moment(filter.value.to, "YYYY-MM-DD")
                                        : null
                                    }
                                    onChange={(date) => {
                                      const currentValue = filter.value || {};
                                      updateFilter(filter.id, "value", {
                                        ...currentValue,
                                        to: date && moment.isMoment(date)
                                          ? date.format("YYYY-MM-DD")
                                          : date instanceof Date
                                            ? moment(date).format("YYYY-MM-DD")
                                            : "",
                                      });
                                    }}
                                    dateFormat="YYYY-MM-DD"
                                    timeFormat={false}
                                    inputProps={{
                                      placeholder: "To date...",
                                      className: "form-control",
                                      style: dateInputStyle,
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="row">
                                <div className="col-6">
                                  <label className="text-white mb-1">
                                    Min Value
                                  </label>
                                  <input
                                    type={
                                      filter.type === "number"
                                        ? "number"
                                        : "text"
                                    }
                                    className="form-control"
                                    placeholder="Min value..."
                                    value={filter.value?.from || ""}
                                    onChange={(e) => {
                                      const currentValue = filter.value || {};
                                      updateFilter(filter.id, "value", {
                                        ...currentValue,
                                        from: e.target.value,
                                      });
                                    }}
                                  />
                                </div>
                                <div className="col-6">
                                  <label className="text-white mb-1">
                                    Max Value
                                  </label>
                                  <input
                                    type={
                                      filter.type === "number"
                                        ? "number"
                                        : "text"
                                    }
                                    className="form-control"
                                    placeholder="Max value..."
                                    value={filter.value?.to || ""}
                                    onChange={(e) => {
                                      const currentValue = filter.value || {};
                                      updateFilter(filter.id, "value", {
                                        ...currentValue,
                                        to: e.target.value,
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            )
                          ) : filter.type === "date" ? (
                            filter.operator === "dynamic" ? (
                              <div style={{ position: 'relative', zIndex: 1040 }}>
                                <Select
                                  activeColor={activeColor}
                                  styles={selectStyles}
                                  className="react-select"
                                  placeholder="Select dynamic option"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  value={
                                    typeof filter.value === "string" && filter.value
                                      ? getDynamicDateOption(filter.value)
                                      : null
                                  }
                                  onChange={(option) =>
                                    updateFilter(
                                      filter.id,
                                      "value",
                                      option?.value || ""
                                    )
                                  }
                                  options={REPORT_FILTER_DYNAMIC_DATE_OPTIONS}
                                />
                              </div>
                            ) : (
                              <div className="react-datetime-container">
                                <ReactDatetime
                                  value={
                                    filter.value ? moment(filter.value, "YYYY-MM-DD") : null
                                  }
                                  onChange={(date) =>
                                    updateFilter(
                                      filter.id,
                                      "value",
                                      date && moment.isMoment(date)
                                        ? date.format("YYYY-MM-DD")
                                        : date instanceof Date
                                          ? moment(date).format("YYYY-MM-DD")
                                          : ""
                                    )
                                  }
                                  dateFormat="YYYY-MM-DD"
                                  timeFormat={false}
                                  inputProps={{
                                    placeholder: "Select date...",
                                    className: "form-control",
                                    style: dateInputStyle,
                                  }}
                                />
                              </div>
                            )
                          ) : (
                            <input
                              type={
                                filter.type === "number" ? "number" : "text"
                              }
                              className="form-control"
                              placeholder="Enter value..."
                              value={filter.value}
                              onChange={(e) =>
                                updateFilter(filter.id, "value", e.target.value)
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>
          <Col md={6} className={closeSourceArea ? "d-none" : ""}>
            <Card
              className="glass-card p-3"
              style={{ maxHeight: "80vh", overflowY: "auto" }}
            >
              <CardTitle
                tag="h5"
                className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"}`}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-database mr-2"></i>
                    Available Fields
                  </div>
                  {!closeSourceArea && (
                    <Button
                      color="link"
                      className="px-2 py-0"
                      style={{ color: isLightTheme ? "black" : "white" }}
                      onClick={() => setCloseSourceArea(!closeSourceArea)}
                    >
                      <i className="tim-icons icon-simple-remove" />
                    </Button>
                  )}
                </div>
              </CardTitle>

              {/* Search Bar */}
              <div className="d-flex justify-content-between align-items-center">
                <div className="mb-2">
                  {filterButtons.map((button) => (
                    <button
                      key={button.key}
                      className={`filter-btn ${activeFilter === button.key ? "active" : ""
                        }`}
                      onClick={() => setActiveFilter(button.key)}
                    >
                      <i className={`${button.icon} mr-0`}></i>
                      {button.showPercent && <span style={{ fontSize: "12px", marginLeft: "2px", fontWeight: "bold" }}>%</span>}
                    </button>
                  ))}
                </div>

                <div className="position-relative mb-2">
                  <input
                    type="text"
                    className="form-control modern-search"
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div
                    className="position-absolute"
                    style={{
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <i
                      className={`fas fa-search ${isLightTheme ? "text-dark" : "text-light"
                        }`}
                    ></i>
                  </div>
                </div>

                {/* Filter Buttons */}
              </div>

              {/* Tables and Fields */}
              <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                {Object.keys(filteredFields)?.map((tableName) => (
                  <div key={tableName} className="mb-0">
                    <div
                      className="table-item d-flex align-items-center justify-content-between"
                      onClick={() =>
                        toggleTableExpansion(tableName)
                      }
                    >
                      <div className="d-flex align-items-center">
                        <i
                          style={{ color: activeColorStyle.borderColor }}
                          className="fas fa-table mr-2"
                        ></i>
                        <h6
                          className={`mb-0 text-capitalize ${isLightTheme ? "text-dark" : "text-white"
                            }`}
                        >
                          {tableName ? tableName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : ''}{" "}
                          <small style={{ opacity: 0.5 }}>
                            {filteredFields[tableName]?.length} fields
                          </small>
                        </h6>
                      </div>
                      <i
                        className={`fas fa-chevron-${expandedTables[tableName] ? "up" : "down"
                          } ${isLightTheme ? "text-dark" : "text-white"}`}
                      />
                    </div>

                    {expandedTables[tableName] && (
                      <div className="ml-3 mt-2">
                        {filteredFields[tableName]?.map((field) => (
                          <div
                            key={field.id}
                            className="field-item p-1 pr-2 d-flex align-items-center justify-content-between"
                            onClick={() => addFilter(field)}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="mr-3">
                                  {getFieldTypeIcon(field.type)}
                                </div>
                                <span
                                  style={{ fontSize: 12 }}
                                  className={
                                    isLightTheme
                                      ? "text-dark"
                                      : "text-white"
                                  }
                                >
                                  {field.label}
                                </span>
                              </div>
                            </div>
                            <i
                              className="fas fa-plus"
                              style={{ color: activeColorStyle.borderColor }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
);

export default ReportFilters;
